import { Heading, HelpText } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { invitesCRUDActions } from '@mm/state/actions/InvitesCRUDActions';
import UsersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import {
  BasicPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import CompanyUsersModel, {
  isLiveChatAgentOrFacebookPageCollaborator,
  NewUserRole,
  UserRole,
} from '@mm/state/model/CompanyUsersModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { selectBasicPages } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeCompanySelector,
  selectActiveCompanyId,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { LifeCycleThis } from '@mm/utils';
import { isEqual } from 'lodash';
import React from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { ButtonGroup } from 'reactstrap';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { Dispatch } from 'redux';
import Button from '../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { FacebookPagesAccess } from '../../../common/components/FacebookPagesAccess/FacebookPagesAccess';
import { UserRoleSelector } from '../../../common/components/UserRoleSelector/UserRoleSelector';
import { RouterComponentProps } from '../../../common/helpers/routes/interfaces';
import { SettingsRoutes } from '../../../routes/settings-routes';
import './UserEditor.pcss';
import { UserFieldEditor } from './UserFieldEditor';

const disabledPageSelection: string[] = [
  UserRole.Owner,
  UserRole.Collaborator,
  UserRole.Admin,
];
type OuterProps = {};
type PropsFromWithState = {
  user: CompanyUsersModel;
  setUser: (user: CompanyUsersModel) => CompanyUsersModel;
  isFetchUser: boolean;
  setIsFetchUser: (isFetchUser: boolean) => void;
  selectedPages: BasicPage[];
  setSelectedPages: (pages: BasicPage[]) => BasicPage[];
  pageSelectionDisabled: boolean;
  setPageSelectionDisabled: (pageSelectionDisabled: boolean) => boolean;
};

type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type PropsBeforeWithHandlers = RouterComponentProps &
  PropsFromWithState &
  PropsFromState &
  PropsFromDispatch;
type PropsBeforeSecondWithHandlers = PropsBeforeWithHandlers & {
  setInitialValues: ReturnType<SetInitialValues>;
};
type InnerProps = PropsBeforeSecondWithHandlers & {
  onPagesSelected: ReturnType<typeof onPagesSelected>;
  getEditable: ReturnType<typeof getEditable>;
  onChangeUser: ReturnType<typeof onChangeUser>;
  onCancel: () => void;
  onSave: (canEditUsers: boolean) => void;
};

type Props = OuterProps & InnerProps;

const ROLE_FIELD_ID = 'role';

const UserEditor = (props: Props) => {
  const {
    onChangeUser,
    user,
    currentUser,
    getEditable,
    activeCompany,
    pages = [],
    selectedPages,
    onPagesSelected,
    pageSelectionDisabled,
    onCancel,
    onSave,
    pagesWithAvailableSeats,
  } = props;

  const availableSeatsMap = pagesWithAvailableSeats.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const fbPages =
    pages.map(page => ({ ...page, disabled: !availableSeatsMap[page.id] })) ||
    [];

  const canEditUsers = !!useSelector(combinedFeatureFlagsSelector).userEdit;

  if (user) {
    const canEditEmail =
      activeCompany && (activeCompany.editable || currentUser.id === user.id);

    return (
      <div className="user-editor-component">
        <div className="user-editor-component__header-content">EDIT USER</div>
        <div className="user-editor-component__header-buttons">
          <ButtonGroup>
            <Button
              invert
              blue
              className="user-editor-component__button"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              blue
              className="user-editor-component__button"
              onClick={() => onSave(canEditUsers)}
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
        <div className="user-editor-component__user">
          <UserFieldEditor
            value={user.firstName}
            fieldType="text"
            fieldName="First Name"
          />
          <UserFieldEditor
            value={user.email}
            fieldType="email"
            fieldName="Email"
            canEdit={canEditEmail}
            onChangeUser={onChangeUser}
          />
          <UserFieldEditor
            value={user.lastName}
            fieldType="text"
            fieldName="Last Name"
          />
          {getEditable() ? (
            <UserRoleSelector
              value={user && user.role}
              onUpdate={value => {
                onChangeUser(ROLE_FIELD_ID, value);
              }}
              showTeamBagde={!canEditUsers}
            />
          ) : (
            <UserFieldEditor
              value={user.role}
              fieldName="Role"
              fieldType="text"
            />
          )}
        </div>

        <div>
          <Heading size={5}>FACEBOOK PAGES</Heading>
          <HelpText>
            A user with the Facebook Page Collaborator or LiveChat Agent role
            will be granted access only to the selected pages below.
            Collaborators and Facebook Page Collaborators will have full control
            over a page's bots, contacts, and other data, but won't be able to
            connect or disconnect Facebook Pages from the system. LiveChat
            Agents will only have access to Inbox on web and mobile app, but
            cannot see and manage bots
          </HelpText>
        </div>

        <div className="user-editor-component__facebook-pages">
          {activeCompany && activeCompany.editable && (
            <FacebookPagesAccess
              pages={fbPages}
              selectedPages={selectedPages}
              onPagesSelected={onPagesSelected}
              disabled={pageSelectionDisabled}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};

type SetInitialValues = (props: PropsBeforeWithHandlers) => () => void;

const setInitialValues: SetInitialValues = props => () => {
  const { setSelectedPages, user, pages, pageSelectionDisabled } = props;

  const allowedPageIds = user && user.allowedFacebookPageIds;

  setSelectedPages(
    pages.filter(
      page =>
        pageSelectionDisabled ||
        !allowedPageIds ||
        allowedPageIds.indexOf(page.id) !== -1,
    ),
  );
};

type OnChangeUser = (
  props: PropsBeforeWithHandlers,
) => (field: string, value: string) => Promise<void>;

const onChangeUser: OnChangeUser =
  ({ setUser, user }) =>
  async (field, value) => {
    const updatedUser = {
      ...user,
      [field]: value,
    };
    if (field === ROLE_FIELD_ID) {
      updatedUser.allowedFacebookPageIds = [];
    }
    setUser(updatedUser);
  };

const stateUpdaters = {
  setUser: () => (user: CompanyUsersModel) => ({ user }),
  setIsFetchUser: () => (isFetchUser: boolean) => ({ isFetchUser }),
  setPageSelectionDisabled: () => (pageSelectionDisabled: boolean) => ({
    pageSelectionDisabled,
  }),
  setSelectedPages: () => (selectedPages: BasicPage[]) => ({ selectedPages }),
};

const initialState: Partial<PropsFromWithState> = {
  isFetchUser: false,
  pageSelectionDisabled: false,
  selectedPages: [] as BasicPage[],
};

function componentDidMount(this: LifeCycleThis<Props>) {
  const {
    getUsers,
    setIsFetchUser,
    match: {
      params: { companyId },
    },
    retrieveAllByCompany,
    user,
    setPageSelectionDisabled,
    pagesWithAvailableSeats,
    fetchAvailableSeats,
  } = this.props;

  if (Number(companyId) !== -1) {
    getUsers(Number(companyId));
    setIsFetchUser(true);
    retrieveAllByCompany(companyId);
  }

  if (!pagesWithAvailableSeats || !pagesWithAvailableSeats.length) {
    fetchAvailableSeats(companyId);
  }

  if (user && disabledPageSelection.indexOf(user.role) !== -1) {
    setPageSelectionDisabled(true);
  }
}

async function componentDidUpdate(
  this: LifeCycleThis<Props>,
  prevProps: Props,
) {
  const { user: prevUser, pages: prevPages } = prevProps;
  const {
    match: {
      params: { userId: strUserId, companyId },
    },
    companyUsers,
    setUser,
    getUsers,
    activeCompanyId,
    companies,
    setInitialValues,
    isFetchUser,
    setIsFetchUser,
    pages,
    user,
    setPageSelectionDisabled,
    updateActiveCompany,
    session,
  } = this.props;

  const companyIdNumber = Number(companyId);

  const haveSetUser = Boolean(prevUser);

  if (!isFetchUser) {
    getUsers(companyIdNumber);
    setIsFetchUser(true);
  }

  if (session && activeCompanyId !== companyIdNumber && companies.length > 0) {
    const nextActiveCompany = companies.find(
      activeCompany => activeCompany.id === companyIdNumber,
    );

    if (nextActiveCompany) {
      updateActiveCompany(nextActiveCompany.id, session.id);
    }
  }

  if (!haveSetUser) {
    const userId = Number(strUserId);
    const editableUser = companyUsers.find(u => u.id === userId);
    if (editableUser) {
      setUser(editableUser);
    }
  }

  if (
    (user && pages && pages.length > 0 && user !== prevUser) ||
    (user && pages !== prevPages)
  ) {
    if (isLiveChatAgentOrFacebookPageCollaborator(user.role)) {
      await setPageSelectionDisabled(false);
      setInitialValues();
    } else {
      await setPageSelectionDisabled(true);
      setInitialValues();
    }
  }
}

const onPagesSelected =
  ({ setSelectedPages, user, setUser }: PropsBeforeWithHandlers) =>
  (selectedPages: BasicPage[]) => {
    const allowedFacebookPageIds = selectedPages.map(page => page.id);

    const updatedUser = {
      ...user,
      allowedFacebookPageIds,
    };

    if (!isEqual(updatedUser, user)) {
      setUser(updatedUser);
    }

    setSelectedPages(selectedPages);
  };

const rolesWithBadgesMap: { [key: string]: boolean } = {
  [UserRole.Admin]: true,
  [UserRole.Collaborator]: true,
  [NewUserRole.Admin]: true,
  [NewUserRole.Collaborator]: true,
};
const rolesWithoutBadgesMap: { [key: string]: boolean } = {
  [UserRole.FacebookPageCollaborator]: true,
  [UserRole.LiveChatAgent]: true,
  [NewUserRole.FacebookPageCollaborator]: true,
  [NewUserRole.LiveChatAgent]: true,
};

const onSave =
  ({
    user,
    updateUserById,
    activeCompanyId,
    getUsers,
    setIsFetchUser,
    history,
    openUpgradeModal,
  }: PropsBeforeWithHandlers) =>
  async (canEditUser: boolean) => {
    if (
      !canEditUser &&
      rolesWithBadgesMap[user.role] &&
      !rolesWithoutBadgesMap[user.role]
    ) {
      return openUpgradeModal();
    }

    const { id, role, allowedFacebookPageIds } = user;

    const success = await updateUserById(activeCompanyId, id, {
      role,
      allowedFacebookPageIds,
    });

    if (success) {
      await getUsers(Number(activeCompanyId));
      setIsFetchUser(true);

      history.push(SettingsRoutes.users.getLink());
    }
  };

const onCancel = (props: PropsBeforeWithHandlers) => () => {
  const { history } = props;

  history.push(SettingsRoutes.users.getLink());
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getUsers: (companyId: number) =>
    dispatch(UsersCRUDActions.retrieveAll(companyId)),
  updateUserById: (
    companyId: number,
    id: number,
    user: Partial<CompanyUsersModel>,
  ) => dispatch(UsersCRUDActions.updateById(companyId, id, user)),
  updateActiveCompany: (id: number, entityId: number) =>
    dispatch(UsersCRUDActions.updateActiveCompanyId(entityId, id)),
  retrieveAllByCompany: (companyId: number) =>
    dispatch(connectedPageCRUDActions.retrieveAllByCompany(companyId)),
  fetchAvailableSeats: (companyId: number) =>
    dispatch(invitesCRUDActions.getAvailableSeats(companyId)),
  openUpgradeModal: () =>
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['SETTINGS__COMPANY-PANEL'],
        planFallback: PageBillingType.TeamLabel,
      }),
    ),
});

const mapStateToProps = (state: StoreState) => ({
  companyUsers: state.companyUsers.entities,
  activeCompanyId: selectActiveCompanyId(state),
  session: state.session.session,
  currentUser: state.session.session,
  companies: selectCompanies(state),
  activeCompany: activeCompanySelector(state),
  pages: selectBasicPages(state),
  pagesWithAvailableSeats: state.invite.pagesWithAvailableSeats,
});

const getEditable =
  ({
    activeCompanyId,
    companies,
    user,
    currentUser,
  }: PropsBeforeWithHandlers) =>
  () => {
    const activeCompany =
      companies && companies.find(c => c.id === activeCompanyId);
    const sameUser = currentUser && user && currentUser.id === user.id;
    const userIsOwner = user && user.role === UserRole.Owner;
    return activeCompany && activeCompany.editable && !sameUser && !userIsOwner;
  };

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({
    getEditable,
    onChangeUser,
    onSave,
    onCancel,
    onPagesSelected,
  }),
  withHandlers({ setInitialValues }),
  lifecycle<Props, {}>({ componentDidMount, componentDidUpdate }),
);

export default enhance(UserEditor);
